import React, { useCallback, useEffect, useRef, useState } from 'react';
import useUomConvert from 'hooks/useUomConvert';
import {
  Button,
  ButtonType,
  Row,
  Input
} from 'syngenta-digital-cropwise-react-ui-kit';
import { Modal } from 'antd';
import { useFormikContext } from 'formik';

import PlusIcon from 'assets/plus.svg?react';
import EditOutlinedIcon from 'assets/edit_outlined.svg?react';
import NoCropZonesSelectedIcon from 'assets/properties-map-icon.svg';
import EmptyTableState from 'components/EmptyTableState';
import FormSection from 'components/FormSection';
import formatNumber from 'helpers/formatNumber';
import {
  FormValues,
  CropZone,
  Product
} from 'screens/Applications&WorkOrders/StepsLayout/Constants/formikData';
import LayoutStrings from 'screens/Applications&WorkOrders/StepsLayout/Constants/layoutStrings';
import SelectPropertiesDrawer from 'screens/YieldV2/Loads/components/LoadsFilterDrawer/components/SelectPropertiesFilter/components/SelectPropertiesDrawer';
import SelectPropertiesDrawerV2 from 'components/SelectPropertiesDrawerV2';
import { getDefaultAreaType } from 'screens/Applications&WorkOrders/StepsLayout/Constants/areaTypeConstants';
import { useAgOperationUtility } from 'screens/Applications&WorkOrders/StepsLayout/Context/AgOperationUtilityProvider';
import { recalculateProductsOnPropertiesChange } from 'screens/Applications&WorkOrders/StepsLayout/Components/ProductsStep/helpers';
import { calculateCarrierTotals } from 'screens/Applications&WorkOrders/StepsLayout/Components/ProductsStep/components/ProductsTable/helpers';
import { Maths } from 'helpers/maths';
import { useFlags } from 'launchdarkly-react-client-sdk';
import featureFlagRenderItem from 'helpers/featureFlagRenderItem';
import { useStore } from 'components/Store';
import useOperationSelectionAdapter from './hooks/useOperationSelectionAdapter';
import CropZonesTable from './components/CropZonesTable';
import {
  calculateCoveragePercent,
  calculateAppliedAcreage,
  calculateTotalArea,
  calculateAverageCoveragePercent,
  calculateTotalAppliedAcreage,
  distributeTotalAcreageProportionally
} from './helpers/cropZoneMath';
import './styles.css';

// Input field dimensions constants
const INPUT_WIDTH = '180px';
const INPUT_HEIGHT = '32px';

// Helper function to ensure minimum value of 1 for validation
const ensureMinimumValue = (value: number): number => {
  return value <= 0 ? 1 : value;
};

const AddCropZonesButton: React.FC<{
  ghost?: boolean;
  onClick: () => void;
}> = ({ ghost = false, onClick }) => (
  <Button
    data-testid={ghost ? undefined : 'select-crop-zones-button'}
    style={{ background: ghost ? 'white' : undefined }}
    type={ButtonType.primary}
    icon={<PlusIcon fill={ghost ? '#14803C' : 'white'} />}
    onClick={onClick}
    ghost={ghost}
  >
    {LayoutStrings.cropZones.selectCropZones}
  </Button>
);

const getMappedData = (data: CropZone[]) =>
  data.map(zone => ({
    ...zone,
    appliedAcreage: zone.appliedAcreage ?? zone.area,
    coveragePercent: zone.coveragePercent ?? 100,
    // Preserve original reportedArea and boundaryArea for area type selection
    reportedArea: zone.reportedArea,
    boundaryArea: zone.boundaryArea || zone.area, // fallback to area if boundaryArea doesn't exist
    // Initialize selectedAreaType if not already set
    selectedAreaType:
      zone.selectedAreaType || getDefaultAreaType(zone.reportedArea)
  }));

/**
 * Computes the new tank info values (totalProduct and tankCount)
 * while keeping overallRatePerAcre constant.
 */
const computeTankInfo = (
  overallRatePerAcre: number,
  totalAcreage: number,
  tankSize: number | undefined
) => {
  const totalProduct = Maths.multiplyRound(overallRatePerAcre, totalAcreage);
  const tankCount = tankSize
    ? Maths.divideSafeRound(totalProduct, tankSize)
    : 0;
  return { totalProduct, tankCount };
};

/**
 * Updates carrier product totals within a product list.
 * Non-carrier products are returned as-is.
 */
const applyCarrierTotals = (
  products: Product[],
  totalAcreage: number,
  tankInfo: {
    overallRatePerAcre: number;
    tankCount: number;
    tankSize: number;
    overallTotalProduct: number;
  },
  convertFn?: Function
): Product[] => {
  // Update carrier's appliedAreaValue before calculating totals
  const withUpdatedAcreage = products.map(p =>
    p.isCarrier
      ? {
          ...p,
          appliedAreaValue: totalAcreage,
          totalAppliedAcreage: totalAcreage
        }
      : p
  );

  const carrierTotals = calculateCarrierTotals(
    withUpdatedAcreage,
    tankInfo,
    convertFn
  );

  return withUpdatedAcreage.map(p =>
    p.isCarrier
      ? {
          ...p,
          totalProduct: carrierTotals.totalProduct,
          ratePerAcre: carrierTotals.ratePerAcre,
          ratePerTank: carrierTotals.ratePerTank,
          totalCost: carrierTotals.totalCost
        }
      : p
  );
};

const PropertiesStep: React.FC = () => {
  const flags = useFlags();
  const {
    state: { organization }
  } = useStore();
  const showNewSelectPropertiesDrawer = featureFlagRenderItem(
    flags.releaseFeNewPropertiesDrawer,
    flags.releaseFeNewPropertiesDrawerByOrg,
    organization?.id
  );

  const {
    values,
    setFieldTouched,
    validateField,
    setFieldValue
  } = useFormikContext<FormValues>();
  const convertFn = useUomConvert();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [totalAppliedAcreage, setTotalAppliedAcreage] = useState(
    values.totalAppliedAcreage || 0
  );
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [totalAcresDisplay, setTotalAcresDisplay] = useState('');
  const [totalCoveragePercent, setTotalCoveragePercent] = useState('');
  // Store actual numeric values with full precision for save operations
  const [totalAcresValue, setTotalAcresValue] = useState(0);
  const [totalCoveragePercentValue, setTotalCoveragePercentValue] = useState(0);
  // Track which field was last edited to determine distribution strategy
  const [lastEditedField, setLastEditedField] = useState<'acres' | 'coverage'>(
    'acres'
  );

  const {
    utilityState: { selectedCropSeasons },
    updateUtilityState
  } = useAgOperationUtility();

  // Keep a ref to the latest Formik values so the callback passed to
  // CropZonesTable always reads fresh state. CropZonesTable's row handlers
  // intentionally omit the callback from their closures to avoid forcing
  // re-renders on every keystroke, which means the closure they capture
  // would go stale after Formik re-renders. The ref sidesteps that problem.
  const valuesRef = useRef(values);
  valuesRef.current = values;

  // Snapshot of zone IDs at the moment the drawer opens. Used by
  // handleCloseDrawer to detect whether the user actually added or removed
  // zones — if the set is unchanged (e.g. opened and cancelled), no total
  // recalculation is needed.
  const drawerInitialZoneIdsRef = useRef<string[] | null>(null);
  useEffect(() => {
    if (isDrawerOpen) {
      drawerInitialZoneIdsRef.current = (valuesRef.current.cropZones ?? []).map(
        z => z.id
      );
    }
  }, [isDrawerOpen]);

  const handleTotalAcreageChange = useCallback(
    (total: number) => {
      const currentValues = valuesRef.current;
      setTotalAppliedAcreage(total);
      setFieldValue('totalAppliedAcreage', total);

      if (!currentValues.products?.length) return;

      // When tank info is enabled, keep overallRatePerAcre constant
      // and recalculate totalProduct, tankCount, and all products
      if (currentValues.isTankInfoEnabled && currentValues.overallRatePerAcre) {
        const { totalProduct, tankCount } = computeTankInfo(
          currentValues.overallRatePerAcre,
          total,
          currentValues.tankSize
        );

        setFieldValue('totalProduct', totalProduct);
        setFieldValue('tankCount', tankCount || undefined);

        const updatedProducts = recalculateProductsOnPropertiesChange(
          currentValues.products,
          total,
          { tankCount },
          convertFn
        );

        const hasCarrier = updatedProducts.some(p => p.isCarrier);
        const finalProducts = hasCarrier
          ? applyCarrierTotals(
              updatedProducts,
              total,
              {
                overallRatePerAcre: currentValues.overallRatePerAcre,
                tankCount,
                tankSize: currentValues.tankSize || 0,
                overallTotalProduct: totalProduct
              },
              convertFn
            )
          : updatedProducts;

        setFieldValue('products', finalProducts);
      } else {
        // No tank info — just recalculate products
        const updatedProducts = recalculateProductsOnPropertiesChange(
          currentValues.products,
          total,
          undefined,
          convertFn
        );
        setFieldValue('products', updatedProducts);
      }
    },
    [setFieldValue, convertFn]
  );

  const handleDataChange = (newData: any[]) => {
    // Update the cropZones array
    setFieldValue('cropZones', newData);
  };

  const handleSelectCropZones = () => {
    setIsDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);

    // Defer reading values until after React processes the drawer's
    // setFieldValue('cropZones', ...) — handleCloseDrawer is called
    // synchronously right after, so valuesRef.current would otherwise still
    // hold the pre-drawer state and we'd compute a stale (often 0) total.
    requestAnimationFrame(() => {
      const currentZones = valuesRef.current.cropZones ?? [];
      const initialIds = drawerInitialZoneIdsRef.current ?? [];
      const currentIds = currentZones.map(z => z.id);

      // Only recompute the total if the user actually added or removed zones.
      // Comparing zone IDs (not totals) is precise — comparing computed sums
      // would re-introduce the floating-point drift the broader fix addresses.
      const zonesChanged =
        currentIds.length !== initialIds.length ||
        currentIds.some(id => !initialIds.includes(id));

      if (zonesChanged) {
        handleTotalAcreageChange(calculateTotalAppliedAcreage(currentZones));
      }
      drawerInitialZoneIdsRef.current = null;

      if (currentZones.length === 0) {
        (document.querySelector(
          '[data-testid="select-crop-zones-button"]'
        ) as HTMLElement | null)?.focus();
      } else {
        (document.querySelector(
          '[data-testid="next-step-button"]'
        ) as HTMLElement | null)?.focus();
      }
    });
  };

  const handleOpenEditModal = () => {
    setEditModalOpen(true);
    // Initialize with current values - format to 2 decimal places for display
    setTotalAcresDisplay(
      Maths.parseFloatRound(totalAppliedAcreage, 2).toFixed(2)
    );
    setTotalAcresValue(totalAppliedAcreage);

    // Calculate average coverage percent from current crop zones
    const avgCoverage = calculateAverageCoveragePercent(values.cropZones || []);

    setTotalCoveragePercent(Maths.parseFloatRound(avgCoverage, 2).toFixed(2));
    setTotalCoveragePercentValue(avgCoverage);
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setTotalAcresDisplay('');
    setTotalCoveragePercent('');
  };

  const handleSaveEditModal = () => {
    if (!values.cropZones || values.cropZones.length === 0) {
      handleCloseEditModal();
      return;
    }

    // Determine distribution mode based on last edited field
    const distributionMode =
      lastEditedField === 'coverage' ? 'uniform' : 'proportional';

    // Use helper function to distribute based on the mode
    const updatedCropZones = distributeTotalAcreageProportionally(
      values.cropZones,
      totalAcresValue,
      totalCoveragePercentValue,
      distributionMode
    );

    // Update the cropZones in Formik first, then commit the user's exact total.
    // Driving the total update explicitly (rather than relying on a reactive
    // sum-of-zones back-propagation) preserves the user's exact input — the
    // sum of zones may differ by floating-point noise after distribution.
    setFieldValue('cropZones', updatedCropZones);
    handleTotalAcreageChange(totalAcresValue);

    handleCloseEditModal();
  };

  const handleTotalAcresBlur = () => {
    const value = Number(totalAcresDisplay);
    if (!Number.isNaN(value)) {
      // Prevent 0 acres - automatically change to 1
      const adjustedValue = ensureMinimumValue(value);

      setTotalAcresValue(adjustedValue); // Store the actual numeric value
      setTotalAcresDisplay(Maths.parseFloatRound(adjustedValue, 2).toFixed(2)); // Display formatted value

      // If we adjusted the value, also recalculate the coverage percent
      if (adjustedValue !== value) {
        const totalArea = calculateTotalArea(values.cropZones || []);
        if (totalArea > 0) {
          const newCoveragePercent = calculateCoveragePercent(
            adjustedValue,
            totalArea
          );
          setTotalCoveragePercent(
            Maths.parseFloatRound(newCoveragePercent, 2).toFixed(2)
          );
          setTotalCoveragePercentValue(newCoveragePercent);
        }
      }
      // Note: Don't recalculate coverage here as it's already done in onChange
    }
  };

  const handleTotalCoveragePercentBlur = () => {
    const value = Number(totalCoveragePercent);
    if (!Number.isNaN(value)) {
      // Prevent 0% coverage - automatically change to 1%
      const adjustedValue = ensureMinimumValue(value);

      setTotalCoveragePercentValue(adjustedValue); // Store the actual numeric value
      setTotalCoveragePercent(
        Maths.parseFloatRound(adjustedValue, 2).toFixed(2)
      ); // Display formatted value

      // If we adjusted the value, also recalculate the applied acreage
      if (adjustedValue !== value) {
        const totalArea = calculateTotalArea(values.cropZones || []);
        if (totalArea > 0) {
          const newTotalAcres = calculateAppliedAcreage(
            adjustedValue,
            totalArea
          );
          setTotalAcresDisplay(
            Maths.parseFloatRound(newTotalAcres, 2).toFixed(2)
          );
          setTotalAcresValue(newTotalAcres);
        }
      }
      // Note: Don't recalculate acres here as it's already done in onChange
    }
  };

  const formatFocusValue = (value: number) =>
    value.toLocaleString('en-US', {
      useGrouping: false,
      maximumFractionDigits: 15
    });

  const handleTotalCoveragePercentFocus = () => {
    setTotalCoveragePercent(formatFocusValue(totalCoveragePercentValue));
  };

  const handleTotalAcresChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setTotalAcresDisplay(inputValue);
    setLastEditedField('acres'); // Track that acres was edited

    const numericValue = Number(inputValue);
    if (!Number.isNaN(numericValue)) {
      setTotalAcresValue(numericValue);

      // Calculate total area of all crop zones using helper
      const totalArea = calculateTotalArea(values.cropZones || []);

      // Calculate and update coverage percent using helper
      if (totalArea > 0) {
        const newCoveragePercent = calculateCoveragePercent(
          numericValue,
          totalArea
        );
        setTotalCoveragePercent(
          Maths.parseFloatRound(newCoveragePercent, 2).toFixed(2)
        );
        setTotalCoveragePercentValue(newCoveragePercent);
      }
    }
  };

  const handleTotalAcresFocus = () => {
    setTotalAcresDisplay(formatFocusValue(totalAcresValue));
  };

  const handleTotalCoveragePercentChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const inputValue = e.target.value;
    setTotalCoveragePercent(inputValue);
    setLastEditedField('coverage'); // Track that coverage was edited

    const numericValue = Number(inputValue);
    if (!Number.isNaN(numericValue)) {
      // Prevent 0% coverage during calculation - use 1% minimum for calculations
      const adjustedValue = ensureMinimumValue(numericValue);
      setTotalCoveragePercentValue(adjustedValue);

      // Calculate total area of all crop zones using helper
      const totalArea = calculateTotalArea(values.cropZones || []);

      // Calculate and update total applied acres using helper
      if (totalArea > 0) {
        const newTotalAcres = calculateAppliedAcreage(adjustedValue, totalArea);
        setTotalAcresDisplay(
          Maths.parseFloatRound(newTotalAcres, 2).toFixed(2)
        );
        setTotalAcresValue(newTotalAcres);
      }
    }
  };

  const isEmptyState = !values.cropZones || values.cropZones.length === 0;

  useEffect(() => {
    // Validate cropZones when it changes
    setFieldTouched('cropZones', true);
    validateField('cropZones');
    if (
      values.cropZones?.length &&
      values.cropZones.some(
        zone => !zone.appliedAcreage && !zone.coveragePercent
      )
    ) {
      setFieldValue('cropZones', getMappedData(values.cropZones));
    }
  }, [values.cropZones]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const newSelectedCropSeasons = JSON.parse(
      localStorage.getItem('selectedCropSeasons') ?? '[]'
    );
    updateUtilityState({ selectedCropSeasons: newSelectedCropSeasons });
    if (isEmptyState) {
      setIsDrawerOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEmptyState]);

  const selectionAdapter = useOperationSelectionAdapter();

  return (
    <>
      {isEmptyState ? (
        <div className="bg-white shadow-sm-card rounded-lg w-full">
          <EmptyTableState
            title={LayoutStrings.cropZones.emptyStateTitle}
            message={LayoutStrings.cropZones.emptyStateMessage}
            overlayIcon={NoCropZonesSelectedIcon}
            button={<AddCropZonesButton onClick={handleSelectCropZones} />}
          />
        </div>
      ) : (
        <div className="flex flex-col w-full">
          <FormSection
            isOptional
            hasWrapper={false}
            autoHeight
            headerContent={
              <div className="mr-8">
                <Row
                  prefixCls="syt-antd-row"
                  className="justify-end text-neutral-60 leading-4 mb-6px"
                >
                  {LayoutStrings.cropZones.totalAppliedAcreage}
                </Row>
                <Row prefixCls="syt-antd-row" className="justify-end text-base">
                  {formatNumber(totalAppliedAcreage)}{' '}
                  {LayoutStrings.cropZones.areaUnit}
                  <Button
                    type={ButtonType.outline}
                    size="small"
                    icon={<EditOutlinedIcon />}
                    className="ml-2"
                    style={{ padding: '2px' }}
                    onClick={handleOpenEditModal}
                  />
                </Row>
              </div>
            }
            button={
              <AddCropZonesButton ghost onClick={handleSelectCropZones} />
            }
          >
            <CropZonesTable
              data={values.cropZones}
              onTotalAcreageChange={handleTotalAcreageChange}
              onDataChange={handleDataChange}
            />
          </FormSection>
        </div>
      )}
      {showNewSelectPropertiesDrawer ? (
        <SelectPropertiesDrawerV2
          open={isDrawerOpen}
          onClose={handleCloseDrawer}
          selectionAdapter={selectionAdapter}
          seasonIds={selectedCropSeasons}
        />
      ) : (
        <SelectPropertiesDrawer
          open={isDrawerOpen}
          onClose={handleCloseDrawer}
          seasonIds={selectedCropSeasons}
          formikValues={{
            cropZoneIds: 'cropZones', // Map to cropZones field, drawer will extract IDs
            totalArea: 'totalArea',
            cropZones: 'cropZones',
            sourceType: 'cropZoneSourceType'
          }}
        />
      )}

      {/* Edit Total Applied Acreage Modal */}
      <Modal
        open={editModalOpen}
        onCancel={handleCloseEditModal}
        footer={null}
        centered
        closable
        width={580}
        transitionName=""
        maskTransitionName=""
        style={{ height: '294px' }}
        styles={{
          body: { height: '100%', display: 'flex', flexDirection: 'column' }
        }}
      >
        <div className="flex flex-col justify-between flex-1">
          <div>
            {/* Title */}
            <div className="text-xl font-semibold leading-7 mt-0">
              {LayoutStrings.cropZones.editModal.title}
            </div>

            {/* Description */}
            <div
              className="mt-2 text-sm font-normal leading-5"
              style={{ color: '#4D5165' }}
            >
              {LayoutStrings.cropZones.editModal.description}
            </div>
          </div>

          {/* Input Fields */}
          <div className="mt-6">
            <div className="flex gap-4">
              <div>
                <label
                  htmlFor="totalAcresDisplay"
                  className="block text-sm font-normal leading-5 mb-0.5"
                  style={{ color: '#696F88' }}
                >
                  {LayoutStrings.cropZones.editModal.totalAppliedAcres}
                </label>
                <Input
                  id="totalAcresDisplay"
                  value={totalAcresDisplay}
                  onChange={handleTotalAcresChange}
                  onFocus={handleTotalAcresFocus}
                  onBlur={handleTotalAcresBlur}
                  suffix={<span className="text-neutral-60">ac</span>}
                  style={{ width: INPUT_WIDTH, height: INPUT_HEIGHT }}
                  autoFocus
                />
              </div>

              <div>
                <label
                  htmlFor="totalCoveragePercent"
                  className="block text-sm font-normal leading-5 mb-0.5"
                  style={{ color: '#696F88' }}
                >
                  {LayoutStrings.cropZones.editModal.totalCoveragePercent}
                </label>
                <Input
                  id="totalCoveragePercent"
                  value={totalCoveragePercent}
                  onChange={handleTotalCoveragePercentChange}
                  onFocus={handleTotalCoveragePercentFocus}
                  onBlur={handleTotalCoveragePercentBlur}
                  suffix={<span className="text-neutral-60">%</span>}
                  style={{ width: INPUT_WIDTH, height: INPUT_HEIGHT }}
                />
              </div>
            </div>
          </div>

          {/* Custom Footer */}
          <div className="flex justify-end gap-2 mt-6 pt-1">
            <Button
              type={ButtonType.outline}
              size="large"
              onClick={handleCloseEditModal}
            >
              {LayoutStrings.cropZones.editModal.cancel}
            </Button>
            <Button
              type={ButtonType.primary}
              size="large"
              onClick={handleSaveEditModal}
            >
              {LayoutStrings.cropZones.editModal.save}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default PropertiesStep;
